import React from 'react';
import styled, { createGlobalStyle, css } from 'styled-components/macro';
import posed, { PoseGroup } from 'react-pose';
import OutsideClickHandler from 'react-outside-click-handler';
import { CloseModal } from './Icons/icons';

const theme = {
  padding: '2.4rem',
  modalWidths: {
    small: 300,
    medium: 600,
    mediumLarge: 650,
    large: 900,
  } as { [index: string]: number },
};

const GlobalStyle = createGlobalStyle<{ isOpen: boolean }>`
${(props) =>
  props.isOpen &&
  css`
    body {
      overflow-y: hidden;
    }
  `};
  
`;

const StyledModal = styled.div`
  z-index: 1000000;
  position: fixed;
  top: 0;
  left: 0;
`;

type OverlayProps = {
  isOpen: boolean;
  children?: React.ReactNode;
  onClose?: (e: React.SyntheticEvent) => void;
  fullHeight?: boolean;
  disabledOutsideClickHandler?: boolean;
};

const AnimatedContent = styled(
  posed.div({
    enter: { y: 0, opacity: 1, transition: { duration: 200, delay: 100 } },
    exit: { y: 20, opacity: 0, transition: { duration: 200 } },
  })
)<{ fullHeight: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100vw;

  z-index: 200000;

  display: flex;
  align-items: ${(props) => (props.fullHeight ? 'flex-start' : 'flex-end')};

  @media only screen and (min-width: 48rem) {
    align-items: center;
  }

  .scroller {
    width: 100vw;
    max-height: 100%;

    @media only screen and (min-width: 48rem) {
      padding: 3rem 0;
    }
    // text-align: center;
    display: flex;
    justify-content: center;
    overflow-y: auto;
    overflow-x: hidden;
    .content {
      height: auto;
      border-radius: 25px;
      text-align: initial;
      display: inline-block;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
    }
  }
  overflow: hidden;
`;

const Shade = styled(
  posed.div({
    enter: { opacity: 1, transition: { duration: 300 } },
    exit: { opacity: 0, transition: { duration: 300 } },
  })
)<{ fullHeight: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100vw;
  z-index: 1800;

  background-color: ${(props) =>
    props.fullHeight ? '#fff' : 'rgba(0, 0, 0, 0.5)'};
`;

export const Overlay = ({
  isOpen,
  children,
  onClose,
  fullHeight,
  disabledOutsideClickHandler = true,
}: OverlayProps) => {
  return (
    <StyledModal>
      <GlobalStyle isOpen={isOpen} />
      <PoseGroup>
        {isOpen && [
          <Shade key="shade" fullHeight={fullHeight} />,
          <AnimatedContent key="modal" fullHeight={fullHeight}>
            <div className="scroller">
              <div className="content">
                <OutsideClickHandler
                  onOutsideClick={(e) => {
                    if (disabledOutsideClickHandler) {
                      onClose && onClose(e);
                    }
                  }}
                >
                  {children}
                </OutsideClickHandler>
              </div>
            </div>
          </AnimatedContent>,
        ]}
      </PoseGroup>
    </StyledModal>
  );
};

type ModalProps = {
  isOpen: boolean;
  title?: string;
  headerImage?: React.ReactNode;
  onClose?: (e: React.SyntheticEvent) => void;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  isClose?: boolean;
  size?: 'small' | 'medium' | 'large' | number | 'mediumLarge' | 'full';
  noPadding?: boolean;
  fullHeight?: boolean;
  style?: any;
  closeIconPosition?: number;
  closeIconRightPosition?: number;
  disabledOutsideClickHandler?: boolean;
  className?: string;
  radius?: boolean;
};

const ModalStyled = styled.div<{
  size: string | number;
  $radius?: boolean;
  $fullHeight?: boolean;
}>`
  display: flex;
  position: relative;
  flex-direction: column;
  background-color: white;
  min-width: 30rem;

  max-width: 100vw;
  width: 100vw;
  height: 100%;
  min-height: ${(props) => (props.$fullHeight ? '100vh' : '100%')};

  @media only screen and (min-width: 48rem) {
    border-radius: 1.1rem;

    width: ${(props) => {
      if (typeof props.size === 'number') return props.size + 'px';
      else return theme.modalWidths[props.size] + 'px';
    }};
  }
  @media screen and (max-width: 47.9rem) {
    border-radius: ${(props) =>
      props.$fullHeight ? '0 0 0 0' : '1.1rem 1.1rem 0 0'};
  }
`;

const ModalTitle = styled.div`
  display: flex;
  font-family: var(--font-primary);
  height: 6rem;
  font-size: 1.6rem;
  box-sizing: border-box;
  justify-content: space-between;
  align-content: center;
  padding: 2rem 3rem 2rem 3rem;
  border-bottom: 1px solid #ccc;
  @media only screen and (min-width: 76.8rem) {
    padding: ${theme.padding};
  }
`;

const ModalHeaderImage = styled.div`
  display: flex;
  box-sizing: border-box;
  justify-content: space-between;
  align-content: center;
  padding: ${theme.padding};
  border-bottom: 1px solid #ccc;
`;

const ModalClose = styled.div<{
  closeIconPosition?: number;
  closeIconRightPosition?: number;
}>`
  z-index: 1;
  right: ${(props) =>
    props.closeIconRightPosition && `${props.closeIconRightPosition}rem`};
  top: ${(props) => props.closeIconPosition && `${props.closeIconPosition}rem`};
  position: absolute;
  font-size: 1.6rem;
  box-sizing: border-box;
  justify-content: flex-end;
  align-content: center;
  .close {
    cursor: pointer;
    width: 2rem;
    height: 2rem;
  }
`;

const ModalBody = styled.div<{ noPadding: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: ${(props) => (props.noPadding ? '0' : '1.6rem')};
  flex: 1 1 auto;
  @media only screen and (min-width: 76.8rem) {
    padding: ${(props) => (props.noPadding ? '0' : theme.padding)};
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem 2rem 2rem 2rem;
`;
export const Modal = ({
  isOpen,
  title,
  headerImage,
  onClose,
  children,
  className,
  isClose = true,
  actions,
  noPadding,
  fullHeight,
  style,
  // radius = true,
  closeIconPosition = 2.4,
  closeIconRightPosition = 2.4,
  size = 'medium',
  disabledOutsideClickHandler = true,
}: ModalProps) => {
  return (
    <Overlay
      isOpen={isOpen}
      onClose={onClose}
      fullHeight={fullHeight}
      disabledOutsideClickHandler={disabledOutsideClickHandler}
    >
      <ModalStyled
        key="modal"
        size={size}
        style={style}
        className={className}
        // radius={radius}
      >
        {title && (
          <ModalTitle>{title && <div>{title.toUpperCase()}</div>}</ModalTitle>
        )}

        {headerImage && <ModalHeaderImage>{headerImage}</ModalHeaderImage>}
        {isClose && (
          <ModalClose
            closeIconRightPosition={closeIconRightPosition}
            closeIconPosition={closeIconPosition}
          >
            <CloseModal onClick={onClose} className="close" />
          </ModalClose>
        )}

        <ModalBody noPadding={noPadding || false}>{children}</ModalBody>
        {actions && <ModalActions>{actions}</ModalActions>}
      </ModalStyled>
    </Overlay>
  );
};
